import React from 'react'
import PropTypes from 'prop-types'

import { Hidden, Link, Typography } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'

import Image from 'artkive/components/Image'
import ContainedSection from 'artkive/components/PageSections/ContainedSection'
import { NAVIGATION } from 'artkive/constants/tracker/mainNavigation'
import useMobileBreakpoint from 'artkive/hooks/useMobileBreakpoint'
import { BREAKPOINTS, PRICING } from 'artkive/utils/constants'
import routes from 'artkive/utils/routes'

import BooksPreview from 'images/ecom/pricing/books.png'
import DigitalPreview from 'images/ecom/pricing/digital.jpg'
import MosaicsPreview from 'images/ecom/pricing/Mosaics.jpg'
import StorageBadge from 'images/ecom/StorageBadge.svg'

import {
  AdditionalBox,
  Card,
  Description,
  GridContainer,
  GridItem,
  ImageWrapper,
  InnerAdditionalBox,
  LearnMoreButtonLink,
  PriceBox,
  PriceText,
  ProductPrice,
  SectionTitle,
  StorageBadgeImage,
  Title,
} from './TransformSection.style'

const useStyles = makeStyles(({ breakpoints }) => ({
  container: {
    [breakpoints.up(BREAKPOINTS.md)]: {
      maxWidth: 900,
      margin: '0 auto',
    },
    [breakpoints.up(BREAKPOINTS.lg)]: {
      maxWidth: 1200,
    },
    [breakpoints.up(1360)]: {
      maxWidth: 1320,
    },
  },
}))

const TransformSection = ({ mosaicPrice }) => {
  const muiTheme = useTheme()
  const isMobile = useMobileBreakpoint()
  const isLargeDesktop = useMediaQuery(muiTheme.breakpoints.up(1360))
  const classes = useStyles()

  return (
    <ContainedSection
      containerClassName={classes.container}
      title={
        <SectionTitle variant='h2' align='center'>
          We Can Transform Your Art Into:
        </SectionTitle>
      }
    >
      <GridContainer container spacing={isLargeDesktop ? 8 : 4}>
        <GridItem xs={12} md={4} item>
          <Card>
            <ImageWrapper>
              <Hidden smDown>
                <StorageBadgeImage src={StorageBadge} alt='+ 3 Months Storage' />
              </Hidden>
              <Image src={BooksPreview} />
            </ImageWrapper>
            <Description>
              <Title variant='h3'>Kids Art Books</Title>
              <PriceBox>
                <PriceText variant='body1'>
                  Starting at&nbsp;
                  <Hidden smDown>
                    <br />
                  </Hidden>
                  <ProductPrice>${PRICING.books.base}</ProductPrice>
                </PriceText>
              </PriceBox>
              <LearnMoreButtonLink
                color='primary'
                href={routes.books}
                size={isMobile ? 'small' : 'large'}
                variant={isMobile ? 'text' : 'outlined'}
                trackerEvent={NAVIGATION.ART_BOOK_CLICK}
                trackerOptions={{
                  section: 'transform_section',
                }}
              >
                Learn More
              </LearnMoreButtonLink>
            </Description>
          </Card>
        </GridItem>
        <GridItem xs={12} md={4} item>
          <Card>
            <ImageWrapper>
              <Hidden smDown>
                <StorageBadgeImage src={StorageBadge} alt='+ 3 Months Storage' />
              </Hidden>
              <Image src={MosaicsPreview} />
            </ImageWrapper>
            <Description>
              <Title variant='h3'>Framed Mosaics</Title>
              <PriceBox>
                <PriceText variant='body1'>
                  Starting at&nbsp;
                  <Hidden smDown>
                    <br />
                  </Hidden>
                  <ProductPrice>${mosaicPrice}</ProductPrice>
                </PriceText>
              </PriceBox>
              <LearnMoreButtonLink
                color='primary'
                href={routes.mosaics}
                size={isMobile ? 'small' : 'large'}
                variant={isMobile ? 'text' : 'outlined'}
                trackerEvent={NAVIGATION.MOSAIC_CLICK}
                trackerOptions={{
                  section: 'transform_section',
                }}
              >
                Learn More
              </LearnMoreButtonLink>
            </Description>
          </Card>
        </GridItem>
        <GridItem xs={12} md={4} item>
          <Card>
            <ImageWrapper>
              <Image src={DigitalPreview} />
            </ImageWrapper>
            <Description>
              <Title variant='h3'>Art Digitization</Title>
              <PriceBox>
                <PriceText variant='body1'>
                  Starting at&nbsp;
                  <Hidden smDown>
                    <br />
                  </Hidden>
                  <ProductPrice>${PRICING.digital.base}</ProductPrice>
                </PriceText>
              </PriceBox>
              <LearnMoreButtonLink
                color='primary'
                href={routes.digital}
                size={isMobile ? 'small' : 'large'}
                variant={isMobile ? 'text' : 'outlined'}
                trackerEvent={NAVIGATION.DIGITAL_CLICK}
                trackerOptions={{
                  section: 'transform_section',
                }}
              >
                Learn More
              </LearnMoreButtonLink>
            </Description>
          </Card>
        </GridItem>
      </GridContainer>
      <AdditionalBox>
        <InnerAdditionalBox>
          <Typography variant='h3'>Keep in Mind</Typography>
          <Typography>
            Each piece of art sent in will be photographed and included in your order. Removing images from a book or
            mosaic proof will not change the cost of your order. If you decide not to print a book or mosaic after the
            art has been photographed, you are responsible for paying the minimum required digitization fee.
          </Typography>
          <Typography>
            Please return your box within {PRICING.books.dueBackMonths} months. If you’re not ready to send your box
            back within {PRICING.books.dueBackMonths} months of your purchase date, no worries—we’ll simply process a $
            {PRICING.books.base} deposit at that time, which covers the first {PRICING.books.minPages} pieces in your
            book. This deposit will be applied toward your final order total once your box is sent in.
          </Typography>
          <Typography>
            Want to see what you’ve paid for so far? You can view your full order details anytime on your{' '}
            <Link href={routes.accountOrders}>
              <strong>My Orders</strong>
            </Link>{' '}
            page.
          </Typography>
        </InnerAdditionalBox>
      </AdditionalBox>
    </ContainedSection>
  )
}

TransformSection.propTypes = {
  mosaicPrice: PropTypes.number.isRequired,
}

export default TransformSection
